import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { isChromeBrowser, isDuckDuckGoBrowser } from '../utils/browserUtils';

/**
 * Custom hook to handle browser navigation events and ensure data is properly refreshed
 * when navigating back to a page, especially in Chrome.
 * 
 * @param queryKeys Array of query keys to invalidate when the component mounts or becomes visible
 */
export function useNavigationRefresh(queryKeys: string[]) {
  const location = useLocation();
  const queryClient = useQueryClient();
  
  useEffect(() => {
    // Function to handle visibility changes (when user navigates back to the tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Invalidate all specified query keys
        queryKeys.forEach(key => {
          queryClient.invalidateQueries({ queryKey: [key] });
        });
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Force refetch when component mounts
    queryKeys.forEach(key => {
      queryClient.invalidateQueries({ queryKey: [key] });
    });
    
    // Apply browser-specific handling
    if (isChromeBrowser()) {
      // Chrome has issues with caching and navigation, so we need to be more aggressive
      // with invalidating queries when the component mounts
      const handlePopState = () => {
        // When navigating with browser back/forward buttons
        queryKeys.forEach(key => {
          queryClient.invalidateQueries({ queryKey: [key] });
        });
      };
      
      window.addEventListener('popstate', handlePopState);
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('popstate', handlePopState);
      };
    }
    
    // For other browsers like DuckDuckGo, the standard visibility change handling is sufficient
    
    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [queryClient, location.key, queryKeys]);
}
